import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
} from "@mui/material";
import { AccessTime, ArrowBack as ArrowBackIcon, BarChart as BarChartIcon, Replay, AutoAwesome as AiIcon } from "@mui/icons-material";
import api from "../../../api";

const STUDENT_BASE = process.env.REACT_APP_STUDENT_PREFIX || "student";

const formatDate = (iso) => {
  if (!iso) return "N/A";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

const formatPct = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return Math.round(n * 10) / 10;
};

const StudentPaperReport = () => {
  const { paperId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  const fetchHistory = useCallback(async () => {
    if (!paperId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/student/paper/history/${paperId}/attempts`);
      setPayload(res.data?.data ?? res.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load attempt history");
    } finally {
      setLoading(false);
    }
  }, [paperId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error || !payload) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || "No data"}</Alert>
        <Button variant="contained" onClick={() => navigate(`/${STUDENT_BASE}/past-papers`)}>
          Back to Practice Papers
        </Button>
      </Box>
    );
  }

  const { paper_name, avg_percentage, avg_score, avg_total_marks, attempts = [] } = payload;
  const submittedAttempts = attempts.filter((a) => a.completed_at);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Button
        size="small"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2, textTransform: "none" }}
        onClick={() => navigate(`/${STUDENT_BASE}/past-papers`)}
      >
        Back to Practice Papers
      </Button>

      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            {paper_name || "Paper"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Average across {submittedAttempts.length} attempt{submittedAttempts.length !== 1 ? "s" : ""}
          </Typography>
          {avg_percentage != null && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
              <Chip
                size="medium"
                icon={<BarChartIcon sx={{ fontSize: 18 }} />}
                label={`Avg ${formatPct(avg_percentage)}%`}
                color={Number(avg_percentage) >= 50 ? "success" : "error"}
                sx={{ fontWeight: 700 }}
              />
              {avg_total_marks > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Avg marks: {Number(avg_score ?? 0).toFixed(1)} / {Number(avg_total_marks).toFixed(0)}
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        Each attempt
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
        <Table size="medium">
          <TableHead>
            <TableRow sx={{ background: "linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)" }}>
              <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Score</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Question details</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {attempts.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                    {row.attempt_number > 1 && <Replay sx={{ fontSize: 16, color: "text.secondary" }} />}
                    <Typography variant="body2">#{row.attempt_number}</Typography>
                    {row.status !== 3 && (
                      <Chip
                        label="PRACTICE"
                        size="small"
                        color="info"
                        sx={{ fontWeight: 700, fontSize: "0.6rem", height: 18 }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>{formatDate(row.completed_at)}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AccessTime sx={{ fontSize: 18, color: "text.secondary" }} />
                    {row.time_taken_sec != null
                      ? `${Math.round(Number(row.time_taken_sec) / 60)} min`
                      : "N/A"}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {row.ai_score != null && row.ai_feedback_status === "completed" ? (
                      <Tooltip title="AI-adjusted score">
                        <Chip
                          size="small"
                          icon={<AiIcon sx={{ fontSize: 13 }} />}
                          label={`${formatPct(row.ai_percentage ?? (row.total_marks > 0 ? Math.round((row.ai_score / row.total_marks) * 1000) / 10 : row.percentage))}%`}
                          color={(row.ai_percentage ?? row.percentage) >= 50 ? "success" : "error"}
                          sx={{ fontWeight: 600 }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        size="small"
                        label={`${formatPct(row.percentage)}%`}
                        color={row.percentage >= 50 ? "success" : "error"}
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {row.total_marks > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
                        Marks:{" "}
                        {row.ai_feedback_status === "completed" && row.ai_score != null
                          ? Number(row.ai_score).toFixed(1)
                          : Number(row.score ?? 0).toFixed(1)}{" "}
                        / {Number(row.total_marks).toFixed(0)}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {row.total_questions != null ? (
                    <Box sx={{ lineHeight: 1.7 }}>
                      {row.top_level_count != null && row.top_level_count !== row.total_questions ? (
                        <>
                          <Typography variant="caption" display="block">Main questions: {row.top_level_count}</Typography>
                          <Typography variant="caption" display="block">Parts graded: {row.total_questions}</Typography>
                        </>
                      ) : (
                        <Typography variant="caption" display="block">
                          Total: {row.top_level_count ?? row.total_questions}
                        </Typography>
                      )}
                      <Typography variant="caption" display="block" sx={{ color: "success.main", fontWeight: 600 }}>
                        Correct: {row.correct_count}
                      </Typography>
                      {row.partial_count > 0 && (
                        <Typography variant="caption" display="block" sx={{ color: "#b45309", fontWeight: 600 }}>
                          Partial: {row.partial_count}
                        </Typography>
                      )}
                      <Typography variant="caption" display="block" sx={{ color: "error.main", fontWeight: 600 }}>
                        Incorrect: {row.incorrect_count}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        Unattended: {row.unanswered_count}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.disabled">—</Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(`/${STUDENT_BASE}/past-paper/${row.id}/result`)}
                    sx={{ mr: 1, textTransform: "none" }}
                  >
                    Report
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(`/${STUDENT_BASE}/past-paper/${row.id}/review`)}
                    sx={{ textTransform: "none" }}
                  >
                    Review
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default StudentPaperReport;
